/**
 * Why a full page load was started. The loop guard counts each reason separately.
 */
export type ReloadReason = 'chunk-load-error' | 'new-release';

const LOOP_GUARD_MILLISECONDS = 30 * 1000;
const STORAGE_KEY_PREFIX = 'reload_once_';

let reloadStarted = false;

/**
 * The browser's page loading, kept apart so that tests can replace it.
 */
export const pageLoader = {
  load(url: string): void {
    // The router passes paths such as /login; going back passes the page already in the address bar
    const target = new URL(url, document.baseURI).href;
    if (target === window.location.href) {
      window.location.reload();
    } else {
      window.location.assign(target);
    }
  },
};

/**
 * Starts a full page load of `url`, so that a stale tab picks up the latest deployment.
 *
 * Several handlers can react to the same failure: on a failed lazy route, the NavigationError
 * handling of ReleaseCheckService and the ErrorHandler both do. The first call decides where the tab goes; later calls in
 * the same page do nothing, so the page the user clicked is not replaced by a reload of the page
 * they were on.
 *
 * The loop guard: if a full page load for the same reason was started in the last 30 seconds, the
 * failure has survived a reload (for example a broken deployment), so no further reload is
 * started. The time is kept in sessionStorage, which survives the reload; without it the guard
 * cannot work, so no reload is started either.
 *
 * Returns true if a full page load is under way, false if the loop guard stopped it; the caller
 * should then treat the error as any other.
 */
export function reloadOnce(url: string, reason: ReloadReason): boolean {
  if (reloadStarted) {
    return true;
  }

  const storageKey = STORAGE_KEY_PREFIX + reason;
  const now = Date.now();
  try {
    const lastReloadAt = Number(sessionStorage.getItem(storageKey));
    if (lastReloadAt && now - lastReloadAt < LOOP_GUARD_MILLISECONDS) {
      return false;
    }
    sessionStorage.setItem(storageKey, now.toString());
  } catch {
    return false;
  }

  reloadStarted = true;
  pageLoader.load(url);
  return true;
}

/**
 * Forgets that a full page load was started in this page. For tests only: in the browser, the
 * page is gone once the load completes.
 */
export function resetReloadOnce(): void {
  reloadStarted = false;
}
